import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Lock, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/clock/public/display`;
const fsEl = () => document.fullscreenElement || document.webkitFullscreenElement;
export const enterFullscreen = () => {
  const el = document.documentElement;
  try { (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el); } catch { /* not supported */ }
};
const exitFullscreen = () => { try { (document.exitFullscreen || document.webkitExitFullscreen)?.call(document); } catch { /* noop */ } };

// PIN-guarded full screen: exiting needs the PIN; if it's left without one, the next tap re-enters
export const useFullscreenLock = (pinEnabled) => {
  const [isFs, setIsFs] = useState(!!fsEl());
  const [locked, setLocked] = useState(false);
  const [prompt, setPrompt] = useState(false);

  useEffect(() => {
    const on = () => {
      const fs = !!fsEl();
      setIsFs(fs);
      if (fs && pinEnabled) {
        setLocked(true);
        // Chrome/Edge: a quick Esc press goes to the page instead of exiting (holding Esc ~2s still exits)
        navigator.keyboard?.lock?.(['Escape']).catch(() => {});
      }
      if (!fs) navigator.keyboard?.unlock?.();
      if (!fs && locked) setPrompt(true);
    };
    document.addEventListener('fullscreenchange', on);
    document.addEventListener('webkitfullscreenchange', on);
    return () => { document.removeEventListener('fullscreenchange', on); document.removeEventListener('webkitfullscreenchange', on); };
  }, [pinEnabled, locked]);

  useEffect(() => {
    if (!locked || !isFs) return undefined;
    const esc = (e) => { if (e.key === 'Escape') { e.preventDefault(); setPrompt(true); } };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [locked, isFs]);

  useEffect(() => {
    if (!locked || isFs) return undefined;
    const back = (e) => { if (!e.target.closest('[data-pin-popup]')) enterFullscreen(); };
    document.addEventListener('pointerdown', back);
    return () => document.removeEventListener('pointerdown', back);
  }, [locked, isFs]);

  const unlock = useCallback(() => { setLocked(false); setPrompt(false); navigator.keyboard?.unlock?.(); if (fsEl()) exitFullscreen(); }, []);
  return { isFs, locked: locked && pinEnabled, prompt, setPrompt, unlock };
};

export const PinPopup = ({ token, onUnlock, onClose, isFs }) => {
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await axios.post(`${API}/${token}/verify-pin`, { pin });
      if (r.data.ok) onUnlock(); else { setErr(r.data.detail || 'Wrong PIN.'); setPin(''); }
    } catch (x) { setErr(x.response?.data?.detail || 'Could not check PIN.'); setPin(''); }
  };
  return (
    <form data-pin-popup onSubmit={submit} className="fixed top-4 right-4 z-[60] w-72 rounded-2xl border border-white/15 bg-slate-950 p-4 text-white shadow-2xl" data-testid="display-pin-popup">
      <button type="button" onClick={onClose} className="absolute top-2 right-2 text-white/50" data-testid="display-pin-close"><X className="h-4 w-4" /></button>
      <p className="flex items-center gap-2 text-sm font-semibold"><Lock className="h-4 w-4" /> Enter PIN to exit full screen</p>
      <input autoFocus inputMode="numeric" type="password" maxLength={6} value={pin} onChange={(e) => { setPin(e.target.value.replace(/\D/g, '')); setErr(''); }}
        className="mt-3 w-full rounded-lg bg-white/10 px-3 py-2 text-center text-2xl tracking-[0.5em] outline-none focus:ring-2 focus:ring-white/40" data-testid="display-pin-input" />
      {err && <p className="mt-2 text-xs text-rose-300" data-testid="display-pin-error">{err}</p>}
      <div className="mt-3 flex gap-2">
        <button type="submit" disabled={pin.length < 4} className="flex-1 rounded-lg bg-white/90 py-2 text-sm font-semibold text-slate-900 disabled:opacity-40" data-testid="display-pin-submit">Unlock</button>
        {!isFs && <button type="button" onClick={() => { enterFullscreen(); onClose(); }} className="flex-1 rounded-lg border border-white/20 py-2 text-sm" data-testid="display-pin-return">Back to full screen</button>}
      </div>
    </form>
  );
};
